import type {
  ConvoSession,
  ConvoSessionWithNotes,
  ConvoSessionNote,
  ConvoNoteAttachment,
  ConvoPartnerProfile,
  CreateConvoSessionRequest,
  UpdateConvoSessionRequest,
  CreateConvoSessionNoteRequest,
  UpsertConvoPartnerProfileRequest,
  ConvoSessionSearchParams,
} from '../types/convo.js';

const BASE_URL = '/api/convo';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
  return response.json() as Promise<T>;
}

// Sessions

export async function getSessions(params?: ConvoSessionSearchParams): Promise<ConvoSession[]> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.situation_type) query.set('situation_type', params.situation_type);
  if (params?.partner_id !== undefined) query.set('partner_id', String(params.partner_id));
  const qs = query.toString();
  return fetchJson<ConvoSession[]>(qs ? `${BASE_URL}/sessions?${qs}` : `${BASE_URL}/sessions`);
}

export async function getSession(id: number): Promise<ConvoSessionWithNotes> {
  return fetchJson<ConvoSessionWithNotes>(`${BASE_URL}/sessions/${id}`);
}

export async function createSession(data: CreateConvoSessionRequest): Promise<ConvoSession> {
  return fetchJson<ConvoSession>(`${BASE_URL}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateSession(id: number, data: UpdateConvoSessionRequest): Promise<ConvoSession> {
  return fetchJson<ConvoSession>(`${BASE_URL}/sessions/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteSession(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/sessions/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}

// Notes

export async function addNote(sessionId: number, data: CreateConvoSessionNoteRequest, attachmentIds?: number[]): Promise<ConvoSessionNote> {
  return fetchJson<ConvoSessionNote>(`${BASE_URL}/sessions/${sessionId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...data, attachmentIds }),
  });
}

export async function deleteNote(sessionId: number, noteId: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/sessions/${sessionId}/notes/${noteId}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}

// Analyze

export async function analyzeSession(id: number, opts?: { provider?: string; model?: string }): Promise<ConvoSessionWithNotes> {
  return fetchJson<ConvoSessionWithNotes>(`${BASE_URL}/sessions/${id}/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: opts ? JSON.stringify(opts) : undefined,
  });
}

// Coach

export async function sendToCoach(sessionId: number, message: string, attachmentIds?: number[]): Promise<{ userNote: ConvoSessionNote; coachNote: ConvoSessionNote }> {
  return fetchJson<{ userNote: ConvoSessionNote; coachNote: ConvoSessionNote }>(`${BASE_URL}/sessions/${sessionId}/coach`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, attachmentIds }),
  });
}

// Attachments

export async function uploadConvoAttachment(sessionId: number, file: File): Promise<ConvoNoteAttachment> {
  const buffer = await file.arrayBuffer();
  const response = await fetch(`${BASE_URL}/sessions/${sessionId}/attachments`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type,
      'X-File-Name': encodeURIComponent(file.name),
    },
    body: buffer,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(typeof body.error === 'string' ? body.error : `Upload failed: ${response.status}`);
  }
  return response.json() as Promise<ConvoNoteAttachment>;
}

export async function deleteConvoAttachment(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/attachments/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(typeof body.error === 'string' ? body.error : `Delete failed: ${response.status}`);
  }
}

export function getConvoAttachmentUrl(id: number): string {
  return `${BASE_URL}/attachments/${id}`;
}

// Partner Profiles

export async function getPartnerProfile(personId: number): Promise<ConvoPartnerProfile | null> {
  try {
    return await fetchJson<ConvoPartnerProfile>(`${BASE_URL}/partner-profiles/${personId}`);
  } catch {
    return null;
  }
}

export async function upsertPartnerProfile(personId: number, data: UpsertConvoPartnerProfileRequest): Promise<ConvoPartnerProfile> {
  return fetchJson<ConvoPartnerProfile>(`${BASE_URL}/partner-profiles/${personId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deletePartnerProfile(personId: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/partner-profiles/${personId}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    const msg = typeof body.error === 'string' ? body.error : body.error?.message ?? `Request failed: ${response.status}`;
    throw new Error(msg);
  }
}

// Email

export interface EmailSearchResult {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  date: string;
  snippet: string;
  account: string;
}

export interface EmailDetail {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  to: string;
  date: string;
  body: string;
  snippet: string;
}

export async function searchEmails(query: string): Promise<EmailSearchResult[]> {
  return fetchJson<EmailSearchResult[]>(`${BASE_URL}/emails/search?query=${encodeURIComponent(query)}`);
}

export async function getEmail(messageId: string, account?: string): Promise<EmailDetail> {
  const params = account ? `?account=${encodeURIComponent(account)}` : '';
  return fetchJson<EmailDetail>(`${BASE_URL}/emails/${messageId}${params}`);
}
